import React, { useState } from "react";
import { Button, ButtonProps } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import JSZip from "jszip";
import { AxiosError } from "axios";
import { EsgExportIcon } from "assets";
import { useAmplitude, useNotify } from "hooks";
import { extractErrorMessage } from "utils";

interface ExportButtonProps<T = any> extends Omit<ButtonProps, "onClick"> {
  /** Function to call the export API */
  exportFunction: (params: T) => Promise<any>;
  /** Parameters to pass to the export function */
  exportParams?: T;
  /** Title to display in the export file */
  exportTitle?: string;
  /** Text to display when not exporting */
  exportText?: string;
  /** Text to display when exporting */
  exportingText?: string;
  /** Success message to show after export */
  successMessage?: string;
  /** Error message to show on failure */
  errorMessage?: string;
  /** Whether to process the response as a ZIP file */
  isZipFile?: boolean;
  /** File extension to filter in ZIP (e.g., '.csv') */
  fileExtension?: string;
  /** Custom success callback */
  onSuccess?: (response: any) => void;
  /** Custom error callback */
  onExportError?: (error: AxiosError) => void;
  /** Whether to open from active tab */
  openFromActiveTab?: boolean;
  /** Validation function called before export. Return error message string to prevent export, or null/undefined to proceed. */
  onBeforeExport?: (params: any) => string | null | undefined;
}

export const ExportButton = <T = any,>({
  exportFunction,
  exportParams,
  exportTitle = "export",
  exportText = "Export",
  exportingText = "Exporting...",
  successMessage = "Export completed successfully.",
  errorMessage = "Failed to export.",
  isZipFile = true,
  fileExtension = ".xlsx",
  onSuccess,
  onExportError,
  children,
  disabled,
  openFromActiveTab,
  onBeforeExport,
  startIcon = <EsgExportIcon />,
  ...buttonProps
}: ExportButtonProps<T>) => {
  const [isExporting, setIsExporting] = useState(false);
  const notify = useNotify();
  const { trackEvent } = useAmplitude();

  const { mutateAsync: exportData } = useMutation({
    mutationFn: exportFunction,
    onMutate: () => {
      setIsExporting(true);
    },
    onSuccess: async (response) => {
      try {
        if (isZipFile) {
          await processZipFile(response);
        } else {
          // Handle non-ZIP files (direct download)
          const blob = new Blob([response], {
            type: "application/octet-stream",
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${exportTitle}${fileExtension}`;
          link.click();
          URL.revokeObjectURL(url);
        }

        setIsExporting(false);
        notify.success(successMessage);
        onSuccess?.(response);
      } catch (error) {
        setIsExporting(false);
        notify.error("Failed to process the downloaded file.");
        onExportError?.(error as AxiosError);
      }
    },
    onError: (error: AxiosError) => {
      setIsExporting(false);
      notify.error(extractErrorMessage(error, errorMessage));
      onExportError?.(error);
    },
  });

  const getMimeType = (
    filename: string,
  ): { mimeType: string; isBinary: boolean } => {
    const ext = filename.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "xlsx":
        return {
          mimeType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          isBinary: true,
        };
      case "csv":
        return { mimeType: "text/csv", isBinary: false };
      case "pdf":
        return { mimeType: "application/pdf", isBinary: true };
      default:
        return { mimeType: "application/octet-stream", isBinary: true };
    }
  };

  const processZipFile = async (response: any) => {
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(response);

    zipContent.forEach(async (relativePath, file) => {
      if (!file.dir) {
        const { mimeType, isBinary } = getMimeType(relativePath);
        const fileContent = await file.async(
          isBinary ? "arraybuffer" : "string",
        );
        const blob = new Blob([fileContent], { type: mimeType });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = relativePath;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
  };

  const handleExport = () => {
    trackEvent(
      "Exported Site",
      `Site Management - ${openFromActiveTab ? "Active" : "Inactive"}`,
    );

    if (onBeforeExport) {
      const validationError = onBeforeExport(exportParams);
      if (validationError) {
        notify.error(validationError);
        return;
      }
    }

    exportData(exportParams ?? ({} as T));
  };

  return (
    <Button
      variant="contained"
      startIcon={startIcon}
      onClick={handleExport}
      disabled={disabled || isExporting}
      {...buttonProps}
      sx={{
        borderRadius: "8px",
        ...(buttonProps.sx as object),
      }}
    >
      {children || (isExporting ? exportingText : exportText)}
    </Button>
  );
};
